import { plainError } from "@/lib/plainError";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, getDocs, where } from "firebase/firestore";
import { orgQuery, getCurrentOrgId } from "@/lib/org";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import imageCompression from "browser-image-compression";
import clsx from "clsx";
import { db, storage, functions } from "@/lib/firebase";
import { useAuth } from "@/store/auth";
import { plainNotifyReason } from "@/lib/notifyReason";
import { fmtYearMonth, fmtHo } from "@/lib/format";
import { openNavigation } from "@/lib/navigation";
import { classifyLine } from "@/lib/apartmentLines";
import { encodeApartmentId, PATTERN_LABEL } from "@/lib/apartmentInfer";
import { useApartmentPattern } from "@/hooks/useApartments";
import { findHousehold } from "@/lib/household";
import { useDongClaims } from "@/hooks/useDongClaims";
import { deliveryBlockReason } from "@/lib/deliveryGuard";
import CancelDelivery from "@/components/CancelDelivery";
import type { Student } from "@/types";
import { BRAND_COLORS } from "@/lib/brands";

// 현장 문제 종류 — 서버(functions/src/reportDeliveryIssue.ts ISSUE_REASONS)와 같은 키
type IssueReason = "short" | "noEntry" | "notFound" | "other";
const ISSUE_CHOICES: Array<[IssueReason, string]> = [
  ["short", "우유 부족"],
  ["noEntry", "출입 불가"],
  ["notFound", "집 못 찾음"],
  ["other", "기타 (메모)"],
];

export default function DeliveryDetail() {
  const { studentId } = useParams();
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const isAdmin = role === "admin";
  const { claims, loading: claimsLoading } = useDongClaims(fmtYearMonth());
  const claimsKey = claims.map((c) => `${c.id}:${c.driverUid}`).join(",");
  const [student, setStudent] = useState<Student | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [done, setDone] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [retryable, setRetryable] = useState(false); // 전송 실패 뒤 같은 사진으로 다시 보낼 수 있게
  const [siblings, setSiblings] = useState<{ sameUnit: Student[]; otherUnit: Student[] }>({ sameUnit: [], otherUnit: [] });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sibState, setSibState] = useState<"loading" | "ready" | "error">("loading");
  const [sibReload, setSibReload] = useState(0);
  const [studentReload, setStudentReload] = useState(0); // 완료 취소 뒤 학생 문서를 다시 읽는다
  // 「문제 있음」 — 미배송으로 두고 사장님 첫 화면에 알림(대표 결정 Q9)
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueReason, setIssueReason] = useState<IssueReason | "">("");
  const [issueMemo, setIssueMemo] = useState("");
  const [issueBusy, setIssueBusy] = useState(false);
  const [issueSent, setIssueSent] = useState(false);
  const [issueErr, setIssueErr] = useState("");
  const [showRaw, setShowRaw] = useState(false); // 명단 원문 펼침
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true; // 다른 학생으로 넘어간 뒤 늦게 온 응답이 화면을 덮지 않게(코덱스 검토)
    setNotFound(false);
    (async () => {
      if (!studentId) return;
      const snap = await getDoc(doc(db, "students", studentId));
      if (!alive) return;
      if (snap.exists()) setStudent({ id: snap.id, ...(snap.data() as any) });
      else setNotFound(true); // 지운 학생·옛 링크 — "불러오는 중"에 영원히 머물지 않게(3차 예행연습 3번)
    })();
    return () => { alive = false; };
  }, [studentId, studentReload]);

  // 형제자매 후보 로드 — 같은 호수(주소) + 같은 보호자 번호를 모두 모아 찾는다.
  // (호수로 묶으면 엄마/아빠 번호가 달라도 같은 집이 잡히고, 번호로도 한 번 더 보강)
  // 형제 조회가 끝나기 전에 찍으면 한 아이만 완료된다(4차 점검 21) — 끝날 때까지 완료 버튼을 잠근다.
  useEffect(() => {
    if (!student) return;
    let alive = true;
    setSelected(new Set([student.id]));
    setSibState("loading");
    if (claimsLoading) return; // 담당 목록이 와야 남의 담당 형제를 거를 수 있다
    (async () => {
      try {
        const queries = [];
        if (student.phone) queries.push(getDocs(orgQuery("students", where("phone", "==", student.phone))));
        if (student.buildingHo) queries.push(getDocs(orgQuery("students", where("buildingHo", "==", student.buildingHo))));
        if (queries.length === 0) { setSiblings({ sameUnit: [], otherUnit: [] }); setSibState("ready"); return; }
        const snaps = await Promise.all(queries);
        if (!alive) return;
        const map = new Map<string, Student>();
        for (const snap of snaps) {
          for (const d of snap.docs) map.set(d.id, { id: d.id, ...(d.data() as any) } as Student);
        }
        const ymNow = fmtYearMonth();
        // 쉬는·택배·남의 담당 형제는 함께 완료하지 않는다 — 서버도 하나라도 섞이면 전부 거부한다
        const avail = [...map.values()].filter(
          (s) => !(s.deliveredMonths || []).includes(ymNow) && !deliveryBlockReason(s, claims, user?.uid, isAdmin),
        );
        const hh = findHousehold(student, avail);
        setSiblings(hh);
        setSelected(new Set([student.id, ...hh.sameUnit.map((s) => s.id)]));
        setSibState("ready");
      } catch (e) {
        if (!alive) return;
        console.warn("형제 조회 실패", e);
        setSiblings({ sameUnit: [], otherUnit: [] });
        setSibState("error");
      }
    })();
    return () => { alive = false; };
  }, [student?.id, student?.phone, student?.buildingHo, sibReload, claimsLoading, claimsKey]);

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  useEffect(() => {
    if (photo) {
      const url = URL.createObjectURL(photo);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [photo]);

  const aptId = student?.buildingName ? encodeApartmentId(student.gu, student.dong, student.buildingName) : null;
  const aptPattern = useApartmentPattern(aptId);

  if (notFound) {
    return (
      <div className="p-6 text-center text-slate-600 space-y-3">
        <p>이 학생을 찾을 수 없습니다. 전출 처리됐거나 옛 링크일 수 있습니다.</p>
        <button className="btn-primary min-h-[44px]" onClick={() => navigate("/driver/route")}>오늘 경로로 돌아가기</button>
      </div>
    );
  }
  if (!student) return <div className="p-6 text-center text-slate-500">불러오는 중… (오래 걸리면 전파를 확인하고 새로 고침)</div>;

  const pattern = aptPattern?.pattern || "1-2|3-4";
  const line = classifyLine(student, pattern);
  const ym = fmtYearMonth();
  // 쉬는 집·택배 집·남의 담당 집은 보여만 주고 완료 버튼을 끈다(4차 점검 18). 서버도 같은 규칙으로 거부한다.
  const blockReason = claimsLoading ? "" : deliveryBlockReason(student, claims, user?.uid, isAdmin);
  const confirmList = [student, ...siblings.sameUnit, ...siblings.otherUnit].filter((s) => selected.has(s.id));
  // 이번 달 이미 완료된 집 — 다시 찍게 두지 않고 취소 칸을 보여 준다(대표 결정 Q6: 기사도 시간 제한 없이 취소)
  const alreadyDone = !done && !uploading && (student.deliveredMonths || []).includes(ym);

  const markCourier = async () => {
    if (done) return; // 방금 배송을 끝낸 집을 택배로 돌리면 택배 목록에 또 들어간다
    if (!window.confirm(`${student.name} 학생 집을 이번 달 택배로 돌릴까요?
기사 배송 목록에서 빠지고 월말 택배 목록에 들어갑니다.`)) return;
    try {
      await updateDoc(doc(db, "students", student.id), { courierFlag: true, courierReason: "month" }); // 기사 말대로 "이번 달" — 새 달 시작 때 풀린다
      setStatus("월말 택배로 전환됨");
      setTimeout(() => navigate("/driver/route"), 800);
    } catch (e) {
      setStatus(`❌ 택배로 돌리지 못했습니다 — ${plainError(e)}`);
    }
  };

  const sendIssue = async () => {
    if (!issueReason || issueBusy) return;
    setIssueBusy(true);
    setIssueErr("");
    try {
      const fn = httpsCallable(functions, "reportDeliveryIssue");
      await fn({ studentIds: confirmList.map((s) => s.id), reason: issueReason, memo: issueMemo.trim(), yearMonth: ym });
      setIssueSent(true);
    } catch (e) {
      setIssueErr(`❌ 알리지 못했습니다 — ${plainError(e)}`);
    } finally {
      setIssueBusy(false);
    }
  };

  const complete = async (photoArg?: File) => {
    const file = photoArg || photo;
    if (!file || !user || uploading || done || blockReason) return;
    const ids = [...selected];
    if (ids.length === 0) return;
    setUploading(true);
    try {
      setStatus("사진 압축 중…");
      // 압축은 사진을 새로 그려 저장하므로 원본에 든 위치 정보(GPS)가 빠진다.
      // 압축이 실패했다고 원본을 올리면 보호자 사진에 집 위치가 같이 나갈 수 있어 올리지 않는다(4차 점검 17).
      let compressed: File | Blob | null = null;
      for (const useWebWorker of [true, false]) {
        try {
          compressed = await imageCompression(file, { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker });
          break;
        } catch (e) {
          console.warn(`사진 압축 실패(useWebWorker=${useWebWorker})`, e);
        }
      }
      if (!compressed) {
        setStatus("❌ 사진을 줄이지 못해 보내지 않았습니다. 「📷 사진 촬영」을 다시 눌러 한 번 더 찍어 주세요.");
        return;
      }

      setStatus("사진 업로드 중…");
      // 사진은 대표(현재) 학생 경로에 1장만 올리고 형제는 같은 사진을 공유
      const path = `orgs/${getCurrentOrgId()}/deliveries/${ym}/${student.id}/${Date.now()}_${file.name.replace(/[^\w.-]/g, "_")}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, compressed, {
        contentType: compressed.type || file.type || "image/jpeg",
        customMetadata: {
          driverUid: user.uid,
          studentId: student.id,
        },
      });
      const photoUrl = await getDownloadURL(storageRef);

      setStatus(`배송 기록 저장 중… (${ids.length}명)`);
      setDone(true); // 사진은 이미 저장됨 — 중복 배송 방지
      let res: { ok?: boolean; delivered?: number; messagesSent?: number; reason?: string } = {};
      try {
        const fn = httpsCallable(functions, "completeHouseholdDelivery");
        const r = await fn({ studentIds: ids, photoPath: path, photoUrl, note, yearMonth: ym });
        res = (r.data as any) || {};
      } catch (e: any) {
        console.warn("배송 처리 함수 호출 실패 — 사진은 저장됨", e);
        setDone(false); // 실패 시 재시도 가능하도록 잠금 해제
        // 서버가 쉬는 집·남의 담당 집이라 거부한 것은 다시 보내도 같다 — 다시 보내기 버튼을 띄우지 않는다
        if (String(e?.code || "").includes("failed-precondition")) {
          setStatus(`🚫 ${plainError(e)}`);
          return;
        }
        setRetryable(true);
        setStatus(`⚠️ 사진은 저장됐지만 배송 기록이 안 됐습니다 — ${plainError(e)} 아래 「다시 보내기」를 누르세요(사진을 다시 찍지 않아도 됩니다).`);
        return;
      }
      if (res.delivered === 0) {
        // 이미 이번 달 배송이 끝난 집 — 서버가 중복 기록을 막았다(문자도 다시 가지 않음).
        setStatus(`ℹ️ 이미 이번 달 배송이 완료된 집입니다. 중복으로 기록하지 않았습니다.`);
      } else if (res.ok) {
        setStatus(`✅ ${res.delivered}명 배송완료! 보호자에게 문자 ${res.messagesSent}통 전송됨.`);
      } else if ((res.reason || "").includes("알림 받지 않음")) {
        // 보호자가 알림을 꺼 둔 집 — 실패가 아니므로 그냥 넘어간다
        setStatus(`✅ ${res.delivered}명 배송완료! 알림 받지 않음으로 설정된 집이라 문자는 보내지 않았습니다.`);
      } else {
        // 문자가 안 갔으면 바로 넘어가지 않는다 — 기사님이 읽고 '목록으로'를 누르게 한다(사장님 첫 화면에도 표시됨)
        setStatus(`✅ ${res.delivered ?? ids.length}명 배송 기록 완료 · ⚠️ 보호자 문자는 못 보냈어요 (${plainNotifyReason(res.reason || "")}). 사장님 화면에 표시됩니다.`);
        return;
      }
      setTimeout(() => navigate("/driver/route"), 1500);
    } catch (e: any) {
      setRetryable(true);
      setStatus(`❌ 사진을 보내지 못했습니다 — ${plainError(e)} 전파가 잡히는 곳에서 「다시 보내기」를 누르세요.`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-xl mx-auto">
      <Link to="/driver/route" className="text-sm text-milk-600">← 경로로</Link>

      {student.deliveryMemo && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 shadow-sm">
          <div className="text-xs font-bold text-amber-700 mb-1">📌 배송 메모 (반드시 확인)</div>
          <div className="text-lg font-bold text-amber-900 whitespace-pre-wrap break-keep">{student.deliveryMemo}</div>
        </div>
      )}

      <div className="card p-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold">{student.school} {student.name}</h1>
            <div className="text-2xl font-semibold text-slate-700 mt-1 break-keep">{student.refinedAddress}</div>
            {/* 화면 주소는 지오코더가 정제한 값이라 원문과 다를 수 있다(번길 누락·동호 소실).
                언제든 원문을 대조할 수 있게 버튼을 두고, 동·호가 둘 다 비어 정보가 실제로 사라진 경우엔 처음부터 펼쳐 둔다. */}
            {student.rawAddress?.trim() && (
              showRaw || (!student.buildingDong && !student.buildingHo) ? (
                <div className="mt-1.5 rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-700 break-keep">
                  <span className="font-semibold text-slate-500">📋 명단 원문 </span>
                  {student.rawAddress.trim()}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowRaw(true)}
                  className="mt-1.5 text-xs text-slate-500 underline underline-offset-2"
                >
                  📋 명단 원문 보기
                </button>
              )
            )}
            <div className="text-xs text-slate-500 mt-1">
              보호자 {student.guardian} · {student.phone}
            </div>
          </div>
          <span className={`chip ${BRAND_COLORS[student.brand] ?? "bg-slate-100"}`}>{student.brand}</span>
        </div>

        {(student.buildingName || student.buildingDong || student.buildingHo) && (
          <div className="mt-3 rounded-2xl bg-brand-50 p-3 text-sm border border-brand-100">
            <div className="flex items-start justify-between">
              <div>
                {student.buildingName && <div className="font-semibold text-brand-800">🏢 {student.buildingName}</div>}
                <div className="flex gap-1.5 items-center flex-wrap mt-1.5">
                  {student.buildingDong && <span className="chip-neutral">{student.buildingDong}동</span>}
                  {student.buildingHo && <span className="chip-neutral">{fmtHo(student.buildingHo)}</span>}
                  {student.buildingName && <span className="chip-warning">🛗 {line.elevatorHint}</span>}
                </div>
              </div>
              {student.buildingName && (
                <span className={clsx("chip text-2xs", aptPattern?.source === "verified" ? "chip-success" : "chip-brand")}>
                  {aptPattern?.source === "verified" ? "확정" : aptPattern?.source === "inferred" ? "추론" : "기본"} {PATTERN_LABEL[pattern].emoji}
                </span>
              )}
            </div>
            {aptPattern?.note && (
              <div className="mt-2 text-xs text-slate-500 border-t border-brand-100 pt-2">📝 {aptPattern.note}</div>
            )}
            {student.buildingName && (
              <Link
                to={`/driver/apartment/${encodeURIComponent(`${student.gu}_${student.dong}_${student.buildingName}`)}`}
                className="text-xs text-brand-600 hover:text-brand-700 mt-2 inline-block"
              >
                전체 동 라인 시각화 →
              </Link>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <button
            className="btn-secondary flex-1"
            onClick={() => openNavigation({ lat: student.lat!, lng: student.lng!, name: student.refinedAddress }, "tmap")}
            disabled={!student.lat}
          >
            티맵 길안내
          </button>
          <button
            className="btn-secondary flex-1"
            onClick={() => openNavigation({ lat: student.lat!, lng: student.lng!, name: student.refinedAddress }, "kakao")}
            disabled={!student.lat}
          >
            카카오내비
          </button>
        </div>
      </div>

      {(siblings.sameUnit.length > 0 || siblings.otherUnit.length > 0) && (
        <div className="card p-4">
          <h3 className="font-bold mb-1">👨‍👩‍👧‍👦 같은 가구 형제자매</h3>
          <p className="text-xs text-slate-500 mb-2">
            체크한 학생은 사진 1장·문자 1통으로 함께 배송완료 처리됩니다.
          </p>
          <label className="flex items-center gap-2 py-1.5 text-sm">
            <input type="checkbox" checked disabled className="accent-milk-500" />
            <span className="font-medium">{student.school} {student.name}</span>
            <span className="text-xs text-slate-400">현재</span>
          </label>
          {siblings.sameUnit.map((s) => (
            <label key={s.id} className="flex items-center gap-2 py-1.5 text-sm">
              <input
                type="checkbox"
                checked={selected.has(s.id)}
                onChange={() => toggleSelected(s.id)}
                className="accent-milk-500"
              />
              <span>{s.school} {s.name}</span>
              {(s.buildingDong || s.buildingHo) && (
                <span className="text-xs text-slate-400">
                  {s.buildingDong && `${s.buildingDong}동 `}{fmtHo(s.buildingHo)}
                </span>
              )}
            </label>
          ))}
          {siblings.otherUnit.map((s) => (
            <label key={s.id} className="flex items-start gap-2 py-1.5 text-sm text-amber-700">
              <input
                type="checkbox"
                checked={selected.has(s.id)}
                onChange={() => toggleSelected(s.id)}
                className="accent-amber-500 mt-0.5"
              />
              <span>
                {s.school} {s.name}
                <span className="block text-xs">⚠️ 주소 다름: {s.refinedAddress}</span>
              </span>
            </label>
          ))}
        </div>
      )}

      {alreadyDone ? (
        <div className="card p-4 border-2 border-emerald-300 bg-emerald-50 space-y-3">
          <h3 className="font-bold text-emerald-800">✅ 이번 달 배송이 끝난 집입니다</h3>
          <p className="text-sm text-emerald-900 break-keep">엉뚱한 집을 찍었거나 실제로 못 드렸으면 아래에서 취소하세요. 취소하면 다시 배송할 수 있습니다.</p>
          <CancelDelivery
            studentId={student.id}
            yearMonth={ym}
            onCanceled={(m) => { setStatus(m); setDone(false); setPhoto(null); setPreview(""); setStudentReload((n) => n + 1); }}
          />
        </div>
      ) : blockReason ? (
        <div className="card p-4 border-2 border-rose-300 bg-rose-50" role="alert">
          <h3 className="font-bold text-rose-700">🚫 이 집은 배송완료로 찍을 수 없습니다</h3>
          <p className="text-base text-rose-800 mt-1 break-keep">{blockReason}</p>
          <Link to="/driver/route" className="btn-secondary w-full mt-3 min-h-[44px]">오늘 경로로 돌아가기</Link>
        </div>
      ) : (
      <div className="card p-4">
        <h3 className="font-bold mb-2">📸 배송 완료 사진</h3>
        <p className="text-xs text-slate-500 mb-2">
          {/* 「알림 받지 않음」 학생은 서버가 문자에서 뺀다(completeHouseholdDelivery) — 안내도 같게 */}
          {confirmList.length > 0 && confirmList.every((s) => s.notifyOptOut === true) ? (
            <>사진을 찍으면 <b>{selected.size}명</b> 배송완료 처리 후 목록으로 돌아갑니다. 이 집은 보호자 알림을 받지 않습니다.</>
          ) : confirmList.some((s) => s.notifyOptOut === true) ? (
            <>사진을 찍으면 <b>{selected.size}명</b> 배송완료 처리 + 보호자 알림 발송 후 목록으로 돌아갑니다(알림 끈 {confirmList.filter((s) => s.notifyOptOut === true).length}명은 빼고 보냄).</>
          ) : (
            <>사진을 찍으면 <b>{selected.size}명</b> 배송완료 처리 + 보호자 알림 발송 후 목록으로 돌아갑니다.</>
          )}
        </p>
        <textarea
          className="input mb-2"
          placeholder="배송 메모 (선택, 촬영 전 입력) - 예: 문앞 놓아둠"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          disabled={uploading || done}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = ""; // 같은 사진을 다시 골라도 다시 뜨게
            if (f) {
              // 촬영 즉시 업로드·완료·알림 — 확인 단계는 대표 결정으로 뺐다(2026-10-31). 잘못 찍으면 「완료 취소」로 되돌린다
              setPhoto(f);
              setRetryable(false);
              setStatus("");
              complete(f);
            }
          }}
          className="hidden"
        />
        {sibState === "error" && (
          <div className="mb-2 rounded-xl border-2 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
            ⚠️ 같은 집 형제를 확인하지 못했습니다(전파 확인). 지금 찍으면 <b>{student.name} 학생만</b> 완료됩니다.
            <button className="btn-secondary w-full mt-2 min-h-[44px]" onClick={() => setSibReload((n) => n + 1)}>🔁 형제 다시 확인</button>
          </div>
        )}
        <button
          className="btn-primary w-full"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || done || sibState === "loading" || claimsLoading}
        >
          {uploading ? "전송 중…" : done ? "처리 완료" : sibState === "loading" || claimsLoading ? "같은 집 형제 확인 중…" : `📷 사진 촬영 → ${selected.size}명 배송완료`}
        </button>
        {retryable && photo && !uploading && !done && (
          <button className="btn-secondary w-full mt-2 min-h-[48px]" onClick={() => { setRetryable(false); complete(photo); }}>
            🔁 같은 사진으로 다시 보내기
          </button>
        )}
        {preview && (
          <div className="mt-3">
            <img src={preview} className="rounded-lg max-h-80 mx-auto" />
          </div>
        )}
      </div>
      )}

      {status && <div className={status.includes("⚠️") ? "text-base text-center text-amber-700 font-semibold" : "text-sm text-center text-slate-600"} role="status">{status}</div>}
      {done && !uploading && status.includes("못 보냈어요") && (
        <Link to="/driver/route" className="btn-primary w-full">다음 집으로 (목록)</Link>
      )}
      {done && !uploading && (
        <CancelDelivery
          studentId={student.id}
          yearMonth={ym}
          onCanceled={(m) => { setStatus(m); setDone(false); setPhoto(null); setPreview(""); setStudentReload((n) => n + 1); }}
        />
      )}

      {!done && !blockReason && !alreadyDone && (
        <div className="card p-4 border border-amber-300">
          <h3 className="font-bold mb-1 text-amber-800">🚧 문제 있음 (배송 못 함)</h3>
          {issueSent ? (
            <div className="space-y-2" role="status">
              <p className="text-base text-amber-900">사장님 첫 화면에 알렸습니다. 이 집은 <b>미배송</b>으로 남고 보호자 문자는 가지 않습니다.</p>
              <Link to="/driver/route" className="btn-primary w-full min-h-[48px]">다음 집으로 (목록)</Link>
            </div>
          ) : !issueOpen ? (
            <>
              <p className="text-sm text-slate-600 mb-2 break-keep">우유가 모자라거나 들어갈 수 없을 때 사진을 찍지 말고 이것을 누르세요. 「배송완료」 문자가 나가지 않습니다.</p>
              <button className="btn-secondary w-full min-h-[48px]" onClick={() => setIssueOpen(true)}>문제 있음</button>
            </>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {ISSUE_CHOICES.map(([k, label]) => (
                  <button
                    key={k}
                    className={clsx("rounded-xl border-2 px-3 min-h-[52px] text-base font-semibold", issueReason === k ? "border-amber-500 bg-amber-100 text-amber-900" : "border-slate-200 bg-white text-slate-700")}
                    onClick={() => setIssueReason(k)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <textarea
                className="input"
                rows={2}
                maxLength={200}
                placeholder={issueReason === "other" ? "무슨 일인지 한 줄 적어 주세요 (꼭)" : "메모 (선택) — 예: 공동현관 비밀번호 바뀜"}
                value={issueMemo}
                onChange={(e) => setIssueMemo(e.target.value)}
              />
              <button
                className="btn-primary w-full min-h-[48px]"
                disabled={!issueReason || issueBusy || (issueReason === "other" && !issueMemo.trim())}
                onClick={sendIssue}
              >
                {issueBusy ? "알리는 중…" : `사장님께 알리기 (${confirmList.length}명 미배송으로 둠)`}
              </button>
              <button className="btn-ghost w-full min-h-[40px]" onClick={() => setIssueOpen(false)}>닫기</button>
              {issueErr && <div className="text-sm text-rose-700" role="alert">{issueErr}</div>}
            </div>
          )}
        </div>
      )}

      {!done && !blockReason && !alreadyDone && (
        <div className="card p-4">
          <h3 className="font-bold mb-1 text-rose-600">📮 월말 택배로 전환</h3>
          <p className="text-sm text-slate-500 mb-3">
            외진 골목, 접근 불가, 시간 대비 효율 낮은 곳은 택배로 돌리면 월말 리스트에 자동 포함됩니다.
          </p>
          <button className="btn-danger w-full" onClick={markCourier}>이 주소는 월말 택배로</button>
        </div>
      )}
    </div>
  );
}
